const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const getToken = () => localStorage.getItem('token') || localStorage.getItem('vincentrix_token') || '';
const getDealerId = () => {
  try {
    for (const key of ['dealership', 'dealer', 'vincentrix_dealer']) {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      if (parsed?.id) return parsed.id;
    }
  } catch (error) {
    console.warn('VinCentrix service-drive dealer lookup failed:', error);
  }
  return '';
};

const authHeaders = () => {
  const token = getToken();
  const dealerId = getDealerId();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(dealerId ? { 'X-Dealership-Id': dealerId } : {})
  };
};

const clean = value => String(value ?? '').trim();
const digits = value => clean(value).replace(/\D/g, '');
const number = value => {
  const parsed = Number(clean(value).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
};
const escapeHtml = value => clean(value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const canonicalKey = key => clean(key).toLowerCase().replace(/[^a-z0-9]/g, '');
const FIELD_ALIASES = {
  firstname: ['firstname','customerfirstname','first','fname'],
  lastname: ['lastname','customerlastname','last','lname'],
  name: ['name','customername','customer'],
  phone: ['phone','cell','cellphone','mobile','customerphone'],
  email: ['email','customeremail'],
  vin: ['vin','vehiclevin'],
  year: ['year','vehicleyear','modelyear'],
  make: ['make','vehiclemake'],
  model: ['model','vehiclemodel'],
  trim: ['trim','vehicletrim'],
  mileage: ['mileage','miles','odometer'],
  appointmentDate: ['appointmentdate','servicedate','date','scheduleddate'],
  appointmentTime: ['appointmenttime','servicetime','time','scheduledtime'],
  serviceType: ['servicetype','service','reason','opcode','appointmentreason'],
  advisor: ['advisor','serviceadvisor','advisorname'],
  appointmentId: ['appointmentid','serviceappointmentid','id']
};

const valueFor = (row, field) => {
  const index = Object.fromEntries(Object.entries(row || {}).map(([key, value]) => [canonicalKey(key), value]));
  for (const alias of FIELD_ALIASES[field] || [field]) {
    if (index[canonicalKey(alias)] !== undefined) return clean(index[canonicalKey(alias)]);
  }
  return '';
};

const normalizeRecord = row => {
  let firstName = valueFor(row, 'firstname');
  let lastName = valueFor(row, 'lastname');
  const fullName = valueFor(row, 'name');
  if ((!firstName || !lastName) && fullName) {
    const parts = fullName.split(/\s+/).filter(Boolean);
    if (!firstName) firstName = parts[0] || '';
    if (!lastName) lastName = parts.slice(1).join(' ') || '';
  }
  return {
    firstName,
    lastName,
    phone: valueFor(row, 'phone'),
    email: valueFor(row, 'email'),
    vin: valueFor(row, 'vin').toUpperCase(),
    vehicleYear: valueFor(row, 'year'),
    vehicleMake: valueFor(row, 'make'),
    vehicleModel: valueFor(row, 'model'),
    vehicleTrim: valueFor(row, 'trim'),
    mileage: valueFor(row, 'mileage'),
    serviceAppointmentDate: valueFor(row, 'appointmentDate'),
    serviceAppointmentTime: valueFor(row, 'appointmentTime'),
    serviceType: valueFor(row, 'serviceType'),
    serviceAdvisor: valueFor(row, 'advisor'),
    serviceAppointmentId: valueFor(row, 'appointmentId')
  };
};

const parseCsvLine = line => {
  const values = [];
  let current = '';
  let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (char === '"') {
      if (quoted && line[i + 1] === '"') { current += '"'; i += 1; }
      else quoted = !quoted;
    } else if (char === ',' && !quoted) {
      values.push(current);
      current = '';
    } else current += char;
  }
  values.push(current);
  return values;
};

const parseInput = text => {
  const raw = clean(text);
  if (!raw) return [];
  if (raw.startsWith('[') || raw.startsWith('{')) {
    const parsed = JSON.parse(raw);
    const rows = Array.isArray(parsed) ? parsed : (parsed.appointments || parsed.records || parsed.data || [parsed]);
    return rows.map(normalizeRecord);
  }
  const lines = raw.split(/\r?\n/).filter(line => line.trim());
  if (lines.length < 2) throw new Error('CSV needs a header row and at least one appointment.');
  const headers = parseCsvLine(lines[0]);
  return lines.slice(1).map(line => {
    const values = parseCsvLine(line);
    const row = {};
    headers.forEach((header, index) => { row[header] = values[index] ?? ''; });
    return normalizeRecord(row);
  });
};

const qualify = record => {
  const reasons = [];
  let score = 0;
  const currentYear = new Date().getFullYear();
  const year = number(record.vehicleYear);
  const mileage = number(record.mileage);
  const phoneDigits = digits(record.phone);

  if (record.vin.length === 17) score += 35; else reasons.push('VIN must be 17 characters');
  if (phoneDigits.length >= 10) score += 25; else reasons.push('No reachable phone');
  if (year) {
    const age = currentYear - year;
    if (age >= 0 && age <= 12) score += 20;
    else if (age <= 16) score += 10;
    else reasons.push('Vehicle age outside preferred range');
  } else score += 8;
  if (mileage) {
    if (mileage <= 120000) score += 15;
    else if (mileage <= 150000) score += 8;
    else reasons.push('Mileage above 150k');
  } else score += 5;
  if (record.serviceAppointmentDate) score += 5;

  const qualified = record.vin.length === 17 && phoneDigits.length >= 10 && score >= 60;
  return { score: Math.min(100, score), qualified, reasons };
};

const fetchCustomers = async () => {
  const response = await fetch(`${API_URL}/api/customers`, { headers: authHeaders() });
  if (!response.ok) throw new Error(`Unable to load existing opportunities (${response.status})`);
  const data = await response.json();
  return data.customers || [];
};

const createOpportunity = async record => {
  const result = qualify(record);
  const payload = {
    firstName: record.firstName || 'Service',
    lastName: record.lastName || 'Customer',
    phone: record.phone,
    email: record.email,
    vin: record.vin,
    vehicleYear: record.vehicleYear,
    vehicleMake: record.vehicleMake,
    vehicleModel: record.vehicleModel,
    vehicleTrim: record.vehicleTrim,
    mileage: record.mileage,
    source: 'Service Drive',
    acquisitionStatus: 'new',
    status: 'New',
    nextAction: 'Sydney: initiate acquisition outreach',
    serviceAppointmentDate: record.serviceAppointmentDate,
    serviceAppointmentTime: record.serviceAppointmentTime,
    serviceAppointmentId: record.serviceAppointmentId,
    serviceAdvisor: record.serviceAdvisor,
    serviceType: record.serviceType,
    acquisitionScore: result.score,
    acquisitionQualifiedAt: new Date().toISOString(),
    acquisitionQualificationReason: 'Service Drive Intake',
    acquisitionAttribution: 'VinCentrix',
    notes: [
      'Created from Service Drive Intake.',
      record.serviceAppointmentDate ? `Service appointment: ${record.serviceAppointmentDate}${record.serviceAppointmentTime ? ` ${record.serviceAppointmentTime}` : ''}` : '',
      record.serviceType ? `Service: ${record.serviceType}` : '',
      record.serviceAdvisor ? `Advisor: ${record.serviceAdvisor}` : ''
    ].filter(Boolean).join('\n')
  };
  const response = await fetch(`${API_URL}/api/customers`, {
    method: 'POST', headers: authHeaders(), body: JSON.stringify(payload)
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `Create failed (${response.status})`);
  }
  return response.json();
};

let preview = [];
let existingCustomers = [];

const ensureStyles = () => {
  if (document.getElementById('vc-service-drive-styles')) return;
  const style = document.createElement('style');
  style.id = 'vc-service-drive-styles';
  style.textContent = `
    .vc-sd-btn{padding:11px 14px;border-radius:10px;border:1px solid rgba(34,211,238,.32);background:rgba(6,182,212,.11);color:#a5f3fc;cursor:pointer;font-weight:800;font-size:12px;display:flex;align-items:center;justify-content:center;gap:7px}
    .vc-sd-modal{position:fixed;inset:0;z-index:2100;background:rgba(0,0,0,.72);backdrop-filter:blur(8px);display:flex;align-items:center;justify-content:center;padding:18px}
    .vc-sd-panel{width:min(980px,97vw);max-height:92vh;overflow:auto;background:linear-gradient(180deg,#07101f,#050b15);border:1px solid rgba(34,211,238,.25);border-radius:20px;box-shadow:0 30px 90px rgba(0,0,0,.65);padding:22px;color:#fff;font-family:-apple-system,BlinkMacSystemFont,"SF Pro Display",sans-serif}
    .vc-sd-kicker{font-size:10px;text-transform:uppercase;letter-spacing:1.8px;color:#22d3ee;font-weight:900}.vc-sd-title{font-size:22px;font-weight:900;margin-top:5px}.vc-sd-note{font-size:11px;color:rgba(255,255,255,.45);margin-top:5px;line-height:1.5}
    .vc-sd-textarea{width:100%;box-sizing:border-box;min-height:170px;margin-top:16px;padding:14px;border-radius:12px;border:1px solid rgba(255,255,255,.12);background:#0b1322;color:#e5eefc;font:12px ui-monospace,SFMono-Regular,Menlo,monospace;resize:vertical}
    .vc-sd-actions{display:flex;gap:9px;flex-wrap:wrap;margin-top:12px}.vc-sd-action{border:1px solid rgba(255,255,255,.1);background:rgba(255,255,255,.05);color:#fff;border-radius:9px;padding:10px 13px;font-weight:800;font-size:11px;cursor:pointer}.vc-sd-primary{background:linear-gradient(135deg,#0891b2,#0e7490);border:0}.vc-sd-create{background:linear-gradient(135deg,#2563eb,#1d4ed8);border:0}
    .vc-sd-summary{display:grid;grid-template-columns:repeat(4,1fr);gap:9px;margin-top:16px}.vc-sd-stat{padding:12px;border:1px solid rgba(255,255,255,.08);border-radius:11px;background:rgba(255,255,255,.035)}.vc-sd-stat b{display:block;font-size:20px}.vc-sd-stat span{font-size:9px;color:rgba(255,255,255,.4);text-transform:uppercase;letter-spacing:.8px}
    .vc-sd-table{margin-top:14px;border:1px solid rgba(255,255,255,.08);border-radius:12px;overflow:hidden}.vc-sd-row{display:grid;grid-template-columns:1.6fr 1.4fr .7fr .9fr 2fr;gap:8px;padding:10px 12px;border-top:1px solid rgba(255,255,255,.06);font-size:10px;align-items:center}.vc-sd-row:first-child{border-top:0}.vc-sd-head{background:rgba(255,255,255,.04);text-transform:uppercase;letter-spacing:.7px;color:rgba(255,255,255,.4);font-weight:800}.vc-sd-good{color:#86efac}.vc-sd-bad{color:#fca5a5}.vc-sd-dupe{color:#fde68a}.vc-sd-status{font-size:11px;margin-top:10px;color:#bae6fd;min-height:16px}
    @media(max-width:768px){.vc-sd-summary{grid-template-columns:1fr 1fr}.vc-sd-row{grid-template-columns:1.4fr 1fr .7fr}.vc-sd-row>div:nth-child(n+4){display:none}.vc-sd-btn{width:100%}}
  `;
  document.head.appendChild(style);
};

const duplicateSet = () => new Set(existingCustomers.map(customer => clean(customer.vin).toUpperCase()).filter(Boolean));

const renderPreview = root => {
  const duplicateVins = duplicateSet();
  const rows = preview.map(record => {
    const q = qualify(record);
    const duplicate = duplicateVins.has(record.vin);
    return { record, ...q, duplicate };
  });
  const qualified = rows.filter(row => row.qualified && !row.duplicate);
  const duplicateCount = rows.filter(row => row.duplicate).length;
  const rejected = rows.filter(row => !row.qualified).length;
  root.querySelector('.vc-sd-summary').innerHTML = `
    <div class="vc-sd-stat"><b>${rows.length}</b><span>Appointments scanned</span></div>
    <div class="vc-sd-stat"><b>${qualified.length}</b><span>Qualified</span></div>
    <div class="vc-sd-stat"><b>${duplicateCount}</b><span>Duplicates blocked</span></div>
    <div class="vc-sd-stat"><b>${rejected}</b><span>Not qualified</span></div>`;
  root.querySelector('.vc-sd-table').innerHTML = `
    <div class="vc-sd-row vc-sd-head"><div>Customer</div><div>Vehicle</div><div>Score</div><div>Status</div><div>Reason</div></div>
    ${rows.slice(0,100).map(({ record, score, qualified: ok, duplicate, reasons }) => {
      const vehicle = [record.vehicleYear, record.vehicleMake, record.vehicleModel].filter(Boolean).join(' ') || record.vin || 'Unknown vehicle';
      const status = duplicate ? 'Duplicate' : ok ? 'Qualified' : 'Rejected';
      const cls = duplicate ? 'vc-sd-dupe' : ok ? 'vc-sd-good' : 'vc-sd-bad';
      const reason = duplicate ? 'VIN already exists in VinCentrix' : ok ? 'Ready for acquisition outreach' : (reasons.join('; ') || 'Below qualification threshold');
      return `<div class="vc-sd-row"><div><b>${escapeHtml([record.firstName,record.lastName].filter(Boolean).join(' ') || 'Service Customer')}</b><br><span style="color:rgba(255,255,255,.35)">${escapeHtml(record.phone)}</span></div><div>${escapeHtml(vehicle)}<br><span style="color:rgba(255,255,255,.3)">${escapeHtml(record.vin)}</span></div><div><b>${score}</b></div><div class="${cls}">${status}</div><div style="color:rgba(255,255,255,.45)">${escapeHtml(reason)}</div></div>`;
    }).join('')}`;
  root.querySelector('.vc-sd-create').disabled = qualified.length === 0;
  root.querySelector('.vc-sd-create').textContent = qualified.length ? `Create ${qualified.length} Qualified Opportunities` : 'No Qualified Opportunities';
  return qualified.map(row => row.record);
};

const sampleCsv = `firstName,lastName,phone,email,vin,year,make,model,mileage,appointmentDate,appointmentTime,serviceType,advisor\nJohn,Smith,4055551234,john@example.com,1HGCM82633A004352,2021,Honda,Accord,48200,2026-09-09,09:30,Oil Change,Mike Jones\nSarah,Davis,4055557788,sarah@example.com,5YJ3E1EA7KF317XXX,2019,Tesla,Model 3,71200,2026-09-09,11:00,Tire Rotation,Amy Lee`;

const openModal = async () => {
  document.querySelector('.vc-sd-modal')?.remove();
  ensureStyles();
  const modal = document.createElement('div');
  modal.className = 'vc-sd-modal';
  modal.innerHTML = `<div class="vc-sd-panel">
    <div style="display:flex;justify-content:space-between;gap:12px;align-items:flex-start"><div><div class="vc-sd-kicker">VC LIVE • Service Drive</div><div class="vc-sd-title">Service Drive Intake</div><div class="vc-sd-note">Paste CSV or JSON appointment data. VinCentrix will validate VIN/contact data, score acquisition fit, block duplicate VINs, and create only qualified opportunities. Creating an opportunity uses the existing VinCentrix customer workflow, which can hand the opportunity to Sydney.</div></div><button class="vc-sd-action vc-sd-close">Close</button></div>
    <textarea class="vc-sd-textarea" placeholder="Paste service appointments here…"></textarea>
    <div class="vc-sd-actions"><button class="vc-sd-action vc-sd-sample">Load Example</button><button class="vc-sd-action vc-sd-primary vc-sd-preview">Scan & Qualify</button><button class="vc-sd-action vc-sd-create" disabled>Create Qualified Opportunities</button></div>
    <div class="vc-sd-status"></div><div class="vc-sd-summary"></div><div class="vc-sd-table"></div>
  </div>`;
  document.body.appendChild(modal);
  const panel = modal.querySelector('.vc-sd-panel');
  const status = panel.querySelector('.vc-sd-status');
  const textarea = panel.querySelector('.vc-sd-textarea');
  let qualifiedRecords = [];

  modal.onclick = event => { if (event.target === modal) modal.remove(); };
  panel.querySelector('.vc-sd-close').onclick = () => modal.remove();
  panel.querySelector('.vc-sd-sample').onclick = () => { textarea.value = sampleCsv; status.textContent = 'Example loaded. Click Scan & Qualify.'; };
  panel.querySelector('.vc-sd-preview').onclick = async () => {
    status.textContent = 'Scanning appointments…';
    try {
      preview = parseInput(textarea.value);
      existingCustomers = await fetchCustomers();
      qualifiedRecords = renderPreview(panel);
      status.textContent = `${preview.length} appointments scanned. Review the results before creating opportunities.`;
    } catch (error) {
      status.textContent = error.message || 'Unable to scan appointments.';
    }
  };
  panel.querySelector('.vc-sd-create').onclick = async () => {
    if (!qualifiedRecords.length) return;
    const button = panel.querySelector('.vc-sd-create');
    button.disabled = true;
    let created = 0;
    let failed = 0;
    status.textContent = `Creating 0 of ${qualifiedRecords.length}…`;
    for (const record of qualifiedRecords) {
      try {
        await createOpportunity(record);
        created += 1;
      } catch (error) {
        console.error('Service Drive opportunity creation failed:', error);
        failed += 1;
      }
      status.textContent = `Created ${created} of ${qualifiedRecords.length}${failed ? ` • ${failed} failed` : ''}…`;
    }
    status.textContent = `Done. ${created} qualified service-drive opportunities created${failed ? `, ${failed} failed` : ''}. The acquisition pipeline will refresh automatically.`;
    existingCustomers = await fetchCustomers().catch(() => existingCustomers);
    qualifiedRecords = renderPreview(panel);
  };
};

const mountButton = () => {
  ensureStyles();
  const workspace = document.querySelector('.vc-acquisition-workspace');
  if (!workspace) return;
  const actions = workspace.querySelector('.vc-acquisition-actions');
  if (!actions || actions.querySelector('.vc-sd-btn')) return;
  const button = document.createElement('button');
  button.className = 'vc-sd-btn';
  button.innerHTML = '<span aria-hidden="true">⚡</span> Service Drive';
  button.onclick = openModal;
  actions.prepend(button);
};

const observer = new MutationObserver(() => mountButton());
observer.observe(document.documentElement, { childList: true, subtree: true });
window.addEventListener('load', mountButton);
setTimeout(mountButton, 800);
